import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { resolveScraperOrigin, resolveScraperUrl } from './utils/scraperUrl';

const SCRAPER_READY = 'SCRAPER_EMBED_READY';
const AUCTION_SESSION = 'AUCTION_EMBED_SESSION';
const SCRAPER_AUTH_REQUIRED = 'SCRAPER_AUTH_REQUIRED';
const SCRAPER_REQUEST_LOGOUT = 'SCRAPER_REQUEST_LOGOUT';
const AUCTION_THEME = 'AUCTION_EMBED_THEME';

const ScraperEmbedPage = () => {
  const { token, user, logout, getCurrentSession } = useAuth();
  const { theme } = useTheme();
  // The theme the frame first painted with; later flips are posted, not
  // baked into the URL, so a toggle never reloads the scraper.
  const initialThemeRef = useRef(theme);
  const iframeRef = useRef(null);
  const [bridgeReady, setBridgeReady] = useState(false);
  const scraperOrigin = useMemo(
    () => resolveScraperOrigin(process.env.REACT_APP_SCRAPER_ORIGIN),
    []
  );
  const scraperEmbedUrl = useMemo(() => {
    const url = resolveScraperUrl(process.env.REACT_APP_SCRAPER_URL);
    url.pathname = '/embed';
    url.search = '';
    url.hash = '';
    // First paint in the same mode as this page; later flips are posted below.
    url.searchParams.set('theme', initialThemeRef.current);
    return url.toString();
  }, []);

  const sendSessionToScraper = useCallback(() => {
    if (!token || !user) return false;
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return false;

    // The refresh token rides along so the embedded scraper can renew the
    // session itself. postMessage is addressed to a single origin, so unlike the
    // old URL handoff this never leaves the browser. Without it the frame's
    // session expires within the hour and it has to ask the parent again.
    const refreshToken = getCurrentSession()?.refresh_token || null;

    frameWindow.postMessage(
      {
        type: AUCTION_SESSION,
        payload: { token, refreshToken, user },
      },
      scraperOrigin
    );
    return true;
  }, [getCurrentSession, scraperOrigin, token, user]);

  useEffect(() => {
    const onMessage = (event) => {
      const frameWindow = iframeRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;
      if (event.origin !== scraperOrigin) return;
      const eventType = event?.data?.type;
      if (eventType === SCRAPER_READY) {
        setBridgeReady(true);
        sendSessionToScraper();
        return;
      }
      if (eventType === SCRAPER_AUTH_REQUIRED) {
        logout();
        return;
      }
      if (eventType === SCRAPER_REQUEST_LOGOUT) {
        logout();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [logout, scraperOrigin, sendSessionToScraper]);

  useEffect(() => {
    if (!bridgeReady) return;
    sendSessionToScraper();
  }, [bridgeReady, sendSessionToScraper]);

  // Keep the embedded scraper in the same light/dark mode as this page.
  useEffect(() => {
    if (!bridgeReady) return;
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.postMessage({ type: AUCTION_THEME, payload: { theme } }, scraperOrigin);
  }, [bridgeReady, scraperOrigin, theme]);

  return (
    <div className="h-[calc(100vh-140px)] min-h-[720px]">
      <iframe
        ref={iframeRef}
        title="Auction Scraper"
        src={scraperEmbedUrl}
        className="w-full h-full rounded-lg border border-hairline bg-surface"
      />
    </div>
  );
};

export default ScraperEmbedPage;
